import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { CModal, H3 } from '@appcues/component-library';
import { Creatable } from '@studio/legacy-components';
import { stripNonAlphaNumericChars } from 'utils/validation';
import FlowTag from 'components/StepIndex/StepView/StepListItem/StepTagControl/FlowTag';
import { isTagNameUnique } from 'helpers/tags';
import { selectFlow } from 'reducers/account/flows';
import {
  selectTagsForFlow,
  selectUnusedTagsForFlow,
} from 'reducers/account/tags';
import { addTagToFlow, removeTagFromFlow } from 'actions/account/flows';
import { asPromised } from 'utils/as-promised';
import { createTag } from 'actions/account/tags';

const StyledModalSection = styled.section`
  padding: 2em;
`;

const TagContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  flex-grow: 1;
  justify-content: flex-start;
  align-items: center;
  pointer-events: none;
  margin-top: 5px;
  > * {
    margin-right: 0.5em;
    margin-top: 0.5em;
    pointer-events: auto;
  }
`;

export const ManageTagsModal = ({
  flowName,
  flowTags = [],
  unusedTags = [],
  onAddTag,
  onRemoveTag,
  onCreateTag,
  onClose,
}) => {
  const tags = [...flowTags, ...unusedTags];

  const handleAddTag = async ({ label, value }) => {
    const trimmed = label.trim();

    if (!value || !trimmed) {
      return;
    }

    const tagIsUnique = isTagNameUnique(trimmed, tags);

    if (tagIsUnique) {
      const id = await onCreateTag(trimmed);
      onAddTag(id);
    } else {
      onAddTag(value);
    }
  };

  const optionValidator = value => {
    const trimmed = value.trim();
    if (!trimmed) {
      return false;
    }
    return isTagNameUnique(trimmed, tags);
  };

  const options = unusedTags.map(({ id, name }) => ({
    value: id,
    label: name,
  }));

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div onClick={e => e.stopPropagation()}>
      <CModal type="small" onClose={onClose}>
        <StyledModalSection>
          <H3 marginBottom="1em">
            Tags for <b>{flowName}</b>
          </H3>
          <Creatable
            formatCreateLabel={value => `Add "${value.trim()}"`}
            isValidNewOption={optionValidator}
            onChange={handleAddTag}
            onInputChange={stripNonAlphaNumericChars}
            options={options}
            placeholder="Select a tag..."
            portal
          />
          <TagContainer>
            {flowTags
              .filter(tag => tag.name != null)
              .map(stepTag => (
                <div role="listitem" key={stepTag.id}>
                  <FlowTag
                    id={stepTag.id}
                    name={stepTag.name}
                    onDelete={onRemoveTag}
                  />
                </div>
              ))}
          </TagContainer>
        </StyledModalSection>
      </CModal>
    </div>
  );
};

ManageTagsModal.propTypes = {
  flowName: PropTypes.string,
  flowTags: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })
  ),
  unusedTags: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })
  ),
  onAddTag: PropTypes.func,
  onRemoveTag: PropTypes.func,
  onCreateTag: PropTypes.func,
  onClose: PropTypes.func,
};

const mapStateToProps = (state, { flowId }) => ({
  flowName: selectFlow(state, flowId).name,
  flowTags: selectTagsForFlow(state, flowId),
  unusedTags: selectUnusedTagsForFlow(state, flowId),
});

const mapDispatchToProps = (dispatch, { flowId }) => ({
  onAddTag: tagId => dispatch(addTagToFlow({ flowId, tagId })),
  onRemoveTag: tagId => dispatch(removeTagFromFlow({ flowId, tagId })),
  onCreateTag: tagName => asPromised(dispatch, createTag(tagName)),
});

const ConnectedManageTagsModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(ManageTagsModal);

ConnectedManageTagsModal.propTypes = {
  flowId: PropTypes.string,
  onClose: PropTypes.func,
};

export default ConnectedManageTagsModal;
